import React, { Component } from "react";
import {
  Button,
  ListGroup,
  Form,
  Modal,
  Spinner,
  Row,
  Col,
  Nav,
} from "react-bootstrap";
import '../assistant/ProcessStatus.css';
import "./styles.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCircle,
  faSpinner,
  faTriangleExclamation,
  faRobot
} from '@fortawesome/free-solid-svg-icons';

import {adriva_ddm_mdm_source_uploadFiles, adriva_getProcessStatus, adriva_getConfig } from '../API';
import LineageDiagram from './DataAttributeLineage';
import ProfilingResults from './ProfilingResults';


// ---------- Converter from Profiling JSON ----------
interface ProfilingJSON {
  profiles: { [tableName: string]: { columns: string[]; dtypes: Record<string, string> } };
  pks: Record<string, string[]>;
  fks: { table: string; column: string; refTable: string; refColumn: string }[];
}

function profilingToSourceTargetJSON(profiling: ProfilingJSON): any {
  const tables: any[] = [];
  Object.entries(profiling.profiles).forEach(([tableName, profile]) => {
    const cols: any[] = profile.columns.map((colName) => ({
      name: colName,
      type: profile.dtypes[colName] || "string",
      pk: profiling.pks[tableName]?.includes(colName) || false,
      fk: profiling.fks.some((fk) => fk.table === tableName && fk.column === colName),
    }));
    tables.push({ name: tableName, columns: cols });
  });
  return { tables };
}

interface SourceSystemData { id: string; code: string; name: string; }

interface MDMSourceManagerProps {
  sources: SourceSystemData[];
  dm: any;
  app: any;
  handleSrcSystemValueChanges: (data: any) => void;
  refreshAppList: () => void;
}

interface MDMSourceManagerState {
  selectedSource: SourceSystemData | null;
  collapsed: boolean;
  showAddModal: boolean;
  showUploadModal: boolean;
  newSource: { code: string; name: string };
  loading: boolean;
  uploadLoading: boolean;
  selectedDMEntityId: string;
  selectedSubTab: 'load' | 'profile' | 'lineage';
  selectedFiles?: FileList | null;
  supportingData: Record<string, any>;

  //Process
  processId: string | null;
  processSteps: any[];
  error: string | null;
  currentStep?: string;
  status?: string;
  finalMessage?: string; 
  showProcessModal?: boolean; 
  processCompleted?: boolean;
}

const DDM_stepList = [
  { step: "DATA_PROFILING", label: "Data Profiling", agent: "Data Profiling Processer" },
  { step: "IDENTIFY_LOOKUP_COLUMN", label: "Lookup Column Detection", agent: "Lookup Column Identifier Agent" },
  { step: "BUILDING_DM", label: "Data Model Generation", agent: "Data Model Builder Agent" },
  { step: "IDENTIFY_MDM_DEDUPLICATION_RULE", label: "Identify De-duplication Rules", agent: "Dedups Rule Identifier Agent" },
  { step: "IDENTIFY_BUSINESS_METADATA", label: "Identify Business Metadata/Glossary", agent: "Business Glossary Identifier Agent" },
  { step: "IDENTIFY_DM_POLICY_MAPPING", label: "Identify Data Quality Policies", agent: "Data Management Policy Identifier Agent" },
  { step: "IDENTIFY_VALIDATION_RULE", label: "Identify Data Validation Rules", agent: "Data Validation Rule Identifier Agent" },
  { step: "IDENTIFY_LOOKUP_VALUE_ANOMALIES", label: "Identify Lookup Value Anomalies", agent: "Lookup Data Anomalies Identifier Agent" },
  { step: "STANDARDIZE_LOOKUP_VALUES", label: "Standardize Lookup Values", agent: "Lookup Data Standardizer Agent" },
  { step: "FINALIZE_DQ_RULES", label: "Fianlize Data Quality Rules", agent: "DQ Rules Finalizer Agent" }
];

export default class MDMSourceManager extends Component<MDMSourceManagerProps, MDMSourceManagerState> {
  pollingInterval: NodeJS.Timeout | null = null;
  state: MDMSourceManagerState = {
    processId: null,
    processSteps: [],
    error: null,
    selectedSource: null,
    collapsed: false,
    showAddModal: false,
    showUploadModal: false,
    newSource: { code: "", name: "" },
    loading: false,
    uploadLoading: false,
    selectedDMEntityId: "",
    selectedSubTab: 'load',
    supportingData: {} as Record<string, any>,
  };

  componentDidMount() {
    const { dm, sources } = this.props;
    this.setState({
      selectedDMEntityId: dm?.[0]?.id || "",
      selectedSource: sources?.[0] || null
    });
  }

  handleAddSource = () => {
    const { newSource } = this.state;
    const { handleSrcSystemValueChanges, sources } = this.props;
    if (!newSource.code.trim() || !newSource.name.trim()) return;

    this.setState({ loading: true });
    sources.push({ id: `${Date.now()}`, code: newSource.code, name: newSource.name });
    handleSrcSystemValueChanges(sources);

    this.setState({ loading: false, showAddModal: false, newSource: { code: "", name: "" } });
  };

  handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) this.setState({ selectedFiles: e.target.files });
  };

  handleUpload = async () => {
    const { selectedFiles, selectedSource, selectedDMEntityId } = this.state;
    const { app } = this.props;
    if (!selectedFiles || !selectedSource || !selectedDMEntityId) return;

    this.setState({ uploadLoading: true });
    try {
      const response = await adriva_ddm_mdm_source_uploadFiles(
        app.nm, app.setup[0].domain, selectedDMEntityId, selectedSource.id, selectedFiles
      );
      if (response && response.success && response.process_id) {
        this.setState({ processId: response.process_id, processSteps: [{ step: "Starting", status: "✅" }] });
        this.pollProcessStatus(response.process_id);
      } else this.setState({ loading: false, error: "File upload failed" });
      this.setState({ showUploadModal: false });
    } catch (err) { console.error("Upload failed", err); } 
    finally { this.setState({ uploadLoading: false }); }
  };

  pollProcessStatus = (processId: string) => {
    this.pollingInterval = setInterval(async () => {
      try {
        const status = await adriva_getProcessStatus(processId);
        if (status) {
          const stepsUI = DDM_stepList.map((stepObj) => {
            const idx = DDM_stepList.findIndex(s => s.step === stepObj.step);
            const currentIdx = DDM_stepList.findIndex(s => s.step === status.current_step);
            let state = "yettostart";
            if (idx < currentIdx) state = "completed";
            else if (idx === currentIdx && status.status === 'failed') state = "failed";
            else if (idx === currentIdx) state = "inprogress";
            return { ...stepObj, status: state };
          });

          this.setState({ processSteps: stepsUI, showProcessModal: true });

          if (["completed", "failed"].includes(status.status)) {
            if (this.pollingInterval) { clearInterval(this.pollingInterval); this.pollingInterval = null; }
            this.setState({
              loading: false,
              finalMessage: status.message || (status.status === "completed" ? "All steps completed successfully!" : "Process failed."),
              processCompleted: true
            });
          }
        }
      } catch (err) { console.error("Polling error", err); if (this.pollingInterval) { clearInterval(this.pollingInterval); this.pollingInterval = null; } this.setState({ loading: false, error: "Error polling status." }); }
    }, 5000);
  };

  getConfigDataForEntity = (entityId: string) => {
    const { supportingData } = this.state;
    if (supportingData[entityId]) return;

    const input = { configType: 'DDM_PROCESS_DATA', appNm: this.props.app.nm, entityId };
    adriva_getConfig(JSON.stringify(input),
      (res: any) => { if (res.success) this.setState({ supportingData: res.data }); },
      (error: any) => { console.log(error); }
    );
  };

  selectSource = (src: SourceSystemData) => {
    const setUpConfigs = this.props.app.setup.filter((c: any) => c.srcId === src.id);
    if (setUpConfigs.length > 0) {
      const entityIds = setUpConfigs.map((config: any) => config.entityId).join(',');
      this.getConfigDataForEntity(entityIds);
    }
    this.setState({ selectedSource: src, selectedSubTab: 'load' });
  }

  renderProcessModal() {
    const { processSteps, finalMessage, processCompleted } = this.state;
    return (
      <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Agent Execution Pipeline</h5>
              <button type="button" className="btn-close" onClick={() => this.setState({ showProcessModal: false })}></button>
            </div>
            <div className="modal-body">
              <div className="process-flow-container">
                {processSteps.map((step, index) => {
                  let statusClass = "pending";
                  let icon = <FontAwesomeIcon icon={faCircle} className="text-secondary" />;
                  let statusLabel = "Yet to Start";
                  if (step.status === "completed" || processCompleted) {
                    statusClass = "completed"; statusLabel = "Completed"; icon = <FontAwesomeIcon icon={faCheckCircle} className="text-success" />;
                  } else if (step.status === "inprogress") {
                    statusClass = "inprogress"; statusLabel = "In-progress"; icon = <FontAwesomeIcon icon={faSpinner} className="text-warning fa-spin" />;
                  } else if (step.status === "failed") {
                    statusClass = "failed"; statusLabel = "Failed"; icon = <FontAwesomeIcon icon={faTriangleExclamation} className="text-danger" />;
                  }
                  return (
                    <div key={index}>
                      <div className={`process-step-box ${statusClass}`}>
                        <div className="process-step-title d-flex align-items-center">
                          <span className="me-2">{icon}</span>
                          <span>{step.label}</span>
                        </div>
                        <div className="process-step-status">{statusLabel}</div>
                        <div className="process-step-agent">
                          <FontAwesomeIcon icon={faRobot} className="me-1 text-purple" /> Agent: {step.agent}
                        </div>
                      </div>
                      {index !== processSteps.length - 1 && <div className="arrow-down" />}
                    </div>
                  );
                })}
                {finalMessage && <div className="alert alert-info mt-3"><strong>Status:</strong> {finalMessage}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { sources, dm, app } = this.props;
    const { selectedSource, collapsed, showAddModal, showUploadModal, newSource, loading, uploadLoading, supportingData, selectedDMEntityId, selectedSubTab } = this.state;

    let sourceJson = null, targetJson = null, srcTargetMap = [], data_profiling_results = null,
    src_dv = null,
    src_dm = null;
    if (selectedSource) {
      let setUpConfig = app.setup.filter((c: any) => c.srcId === selectedSource.id && c.entity === selectedDMEntityId);
      if (setUpConfig?.length > 0) setUpConfig = setUpConfig.at(-1);
      if (setUpConfig && supportingData[setUpConfig.entityId]) {
        sourceJson = profilingToSourceTargetJSON(supportingData[setUpConfig.entityId]?.high_stats);
        data_profiling_results = supportingData[setUpConfig.entityId]?.data_profiling_results;
        srcTargetMap = supportingData[setUpConfig.entityId]?.data_lineage?.mappings;
      }
      if (selectedDMEntityId) targetJson = dm.find((d: any) => d.id === selectedDMEntityId);
    }

    return (
      <div className="d-flex">
        {/* Sidebar */}
        <div className={`sidebar bg-light border-end ${collapsed ? "collapsed" : ""}`}>
          <div className="sidebar-header d-flex justify-content-between align-items-center p-2">
            <h6 className="mb-0">MDM Sources</h6>
            <Button size="sm" variant="outline-secondary" onClick={() => this.setState({ collapsed: !collapsed })}>{collapsed ? "»" : "«"}</Button>
          </div>
          {!collapsed && <>
            <Button variant="primary" size="sm" className="m-2" onClick={() => this.setState({ showAddModal: true })}>+ Add Source</Button>
            <ListGroup variant="flush">
              {sources.map((src) => (
                <ListGroup.Item key={src.id} action active={selectedSource?.id === src.id} onClick={() => this.selectSource(src)}>
                  <div><strong>{src.code}</strong></div>
                  <small>{src.name}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </>}
        </div>

        {/* Main Content */}
        <div className="flex-grow-1 px-3 pt-1 pb-3">
          <Row className="mb-3">
            <Col>
              <Nav variant="pills" activeKey={selectedDMEntityId} onSelect={(k) => this.setState({ selectedDMEntityId: k || "" })} className="mb-1 mt-1 justify-content-start shadow-sm px-2 py-2 bg-white rounded border" style={{ gap: "1rem" }}>
                {dm.map((t: any) => (
                  <Nav.Item key={t.id}>
                    <Nav.Link eventKey={t.id} className="fw-semibold text-nowrap"
                      style={{
                        backgroundColor: selectedDMEntityId === t.id ? "#319795" : "#f8f9fa",
                        color: selectedDMEntityId === t.id ? "#ffffff" : "#343a40",
                        borderRadius: "0.5rem", padding: "0.5rem 1rem",
                        transition: "all 0.2s ease-in-out",
                        boxShadow: selectedDMEntityId === t.id ? "0 0 6px rgba(90,103,216,0.5)" : "none"
                      }}>{t.label}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Col>
          </Row>

          {selectedSource ? (
            <>
              <h4>Source: {selectedSource.name}</h4>

              {/* Sub-tabs */}
              <Nav variant="tabs" activeKey={selectedSubTab} onSelect={(k) => this.setState({ selectedSubTab: k as any })} className="mb-3">
                <Nav.Item><Nav.Link eventKey="load">Source Data Load</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="profile">Data Profile Results</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="lineage">Data Lineage</Nav.Link></Nav.Item>
              </Nav>

              {/* Sub-tab content */}
              {selectedSubTab === "load" && (
                <Button variant="success" onClick={() => this.setState({ showUploadModal: true })}>Upload CSVs</Button>
              )}
              {selectedSubTab === "profile" && data_profiling_results && (
                <ProfilingResults profiling_results={data_profiling_results.profiling_results} record_store={data_profiling_results.record_store}/> 

              )}
              {selectedSubTab === "lineage" && sourceJson && targetJson && (
                <LineageDiagram sourceJson={sourceJson} targetJson={targetJson} lineage={srcTargetMap} />
              )}
            </>
          ) : <div className="text-muted">Select a source to view details</div>}
        </div>

        {/* Add Source Modal */}
        <Modal className="modal-offset" show={showAddModal} onHide={() => this.setState({ showAddModal: false })}>
          <Modal.Header closeButton><Modal.Title>Add MDM Source</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group><Form.Label>Source Code</Form.Label>
                <Form.Control type="text" value={newSource.code} onChange={(e) => this.setState({ newSource: { ...newSource, code: e.target.value } })} />
              </Form.Group>
              <Form.Group className="mt-2"><Form.Label>Source Name</Form.Label>
                <Form.Control type="text" value={newSource.name} onChange={(e) => this.setState({ newSource: { ...newSource, name: e.target.value } })} />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showAddModal: false })}>Cancel</Button>
            <Button variant="primary" onClick={this.handleAddSource} disabled={loading}>{loading ? <Spinner size="sm" animation="border" /> : "Add"}</Button>
          </Modal.Footer>
        </Modal>

        {/* CSV Upload Modal */}
        <Modal className="modal-offset" show={showUploadModal} onHide={() => this.setState({ showUploadModal: false })}>
          <Modal.Header closeButton><Modal.Title>Upload CSV Files</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group><Form.Label>Select CSV files</Form.Label>
              <Form.Control type="file" accept=".csv" multiple onChange={this.handleFileChange} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showUploadModal: false })}>Cancel</Button>
            <Button variant="primary" onClick={this.handleUpload} disabled={uploadLoading}>{uploadLoading ? <Spinner size="sm" animation="border" /> : "Upload"}</Button>
          </Modal.Footer>
        </Modal>

        {this.state.showProcessModal && this.renderProcessModal()}
      </div>
    );
  }
}
